/**
 * GameList Component
 * Displays list of games in staging_library with filters
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useGames } from '../../hooks/useGames';
import { Loading } from '../Common/Loading';
import { ErrorMessage } from '../Common/ErrorMessage';
import { StatusBadge } from './StatusBadge';
import { ReleaseChannelBadge } from './ReleaseChannelBadge';
import { BetaFeedbackBadge } from './BetaFeedbackBadge';
import {
  STATUS_LABELS,
  DEFAULT_PAGE_SIZE,
  LANGUAGES,
  RELEASE_CHANNEL_LABELS
} from '../../utils/constants';
import { format } from 'date-fns';

export const GameList = () => {
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);

  // Separate state for search input (debounced)
  const [searchInput, setSearchInput] = useState('');

  const [filters, setFilters] = useState({
    status: '',
    language: '',
    releaseChannel: '',
    search: ''
  });

  // Track if search input had focus
  const [searchWasFocused, setSearchWasFocused] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      // Remember if search was focused before triggering refetch
      setSearchWasFocused(document.activeElement === searchInputRef.current);
      setFilters(prev => ({ ...prev, search: searchInput }));
      setPage(0);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, error, refetch } = useGames({
    ...filters,
    limit: rowsPerPage,
    offset: page * rowsPerPage
  });

  // Refocus search input after data loads if it was focused before
  useEffect(() => {
    if (searchWasFocused && searchInputRef.current && !isLoading) {
      searchInputRef.current.focus();
      setSearchWasFocused(false);
    }
  }, [isLoading, searchWasFocused]);

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setPage(0); // Reset to first page when filtering
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  if (isLoading) return <Loading message="Loading games..." />;
  if (error) return <ErrorMessage error={error} onRetry={refetch} />;

  const games = data?.games || [];
  const totalCount = data?.totalCount || 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Game Management</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={refetch}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/games/new')}
          >
            New Game
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Search"
            placeholder="Game name or code..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            inputRef={searchInputRef}
            sx={{ flex: 1 }}
          />

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Language</InputLabel>
            <Select
              value={filters.language}
              label="Language"
              onChange={(e) => handleFilterChange('language', e.target.value)}
            >
              <MenuItem value="">All Languages</MenuItem>
              {LANGUAGES.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>{lang.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 140 }}>
            <InputLabel>Channel</InputLabel>
            <Select
              value={filters.releaseChannel}
              label="Channel"
              onChange={(e) => handleFilterChange('releaseChannel', e.target.value)}
            >
              <MenuItem value="">All Channels</MenuItem>
              {Object.entries(RELEASE_CHANNEL_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Game Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Lang</TableCell>
              <TableCell>Channel</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Contributor</TableCell>
              <TableCell>Modified</TableCell>
              <TableCell>Feedback</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {games.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No games found
                </TableCell>
              </TableRow>
            ) : (
              games.map((game) => (
                <TableRow key={game.id} hover>
                  <TableCell>{game.gameCode}</TableCell>
                  <TableCell>{game.gameName}</TableCell>
                  <TableCell>{game.language}</TableCell>
                  <TableCell>
                    {game.releaseChannel && <ReleaseChannelBadge channel={game.releaseChannel} />}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={game.status} />
                  </TableCell>
                  <TableCell>{game.contributorHandle}</TableCell>
                  <TableCell>
                    {game.modifiedAt ? format(new Date(game.modifiedAt), 'MMM d, yyyy') : '-'}
                  </TableCell>
                  <TableCell>
                    {game.feedbackSummary ? (
                      <BetaFeedbackBadge
                        positiveCount={game.feedbackSummary.tested_ok || 0}
                        negativeCount={game.feedbackSummary.has_issues || 0}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/games/${game.id}`)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={game.status === 'wip' ? 'Edit' : 'Cannot edit in this status'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/games/${game.id}/edit`)}
                            disabled={game.status !== 'wip'}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
};
